import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../helpers/AxiosHelper';

export const voipSlice = createApi({
  reducerPath: 'voip',
  baseQuery: axiosBaseQuery({
    baseURL: process.env.REACT_APP_V3_BASE_URL,
  }),
  tagTypes: ['Voip', 'VoipExtensions'],
  endpoints: (builder) => ({
    // -------------------------------
    // Domains
    // -------------------------------
    getDomains: builder.query({
      query: () => ({ url: '/voip/domains', method: 'get' }),
      providesTags: ['Voip'],
    }),

    getDomainById: builder.query({
      query: ({ domainUuid }) => ({ url: `/voip/domains/${domainUuid}`, method: 'get' }),
      providesTags: ['Voip'],
    }),

    // -------------------------------
    // Extensions
    // -------------------------------
    getExtensionsByDomain: builder.query({
      query: ({ domainUuid }) => ({ url: `/voip/extensions/${domainUuid}`, method: 'get' }),
      providesTags: ['Voip'],
    }),

    getExtensionById: builder.query({
      query: ({ domainUuid, extension }) => ({
        url: `/voip/extensions/${domainUuid}/${extension}`,
        method: 'get',
      }),
      providesTags: ['Voip'],
    }),

    // -------------------------------
    // Voicemails
    // -------------------------------
    getVoicemailsByDomain: builder.query({
      query: ({ domainUuid }) => ({ url: `/voip/voicemails/${domainUuid}`, method: 'get' }),
      providesTags: ['Voip'],
    }),

    getVoicemailsByExtension: builder.query({
      query: () => ({
        url: '/voip/voicemails',
        method: 'get',
      }),
      providesTags: ['Voip'],
    }),

    getVoicemailPlayUrl: builder.query({
      query: ({ voicemailMessageId }) => ({
        url: `/voip/voicemail/${voicemailMessageId}`,
        method: 'get',
      }),
    }),

    // -------------------------------
    // Call Detail Records (CDRs)
    // -------------------------------
    getCdrs: builder.query({
      query: (filters = {}) => ({ url: '/voip/cdrs', method: 'get', params: filters }),
      providesTags: ['Voip'],
    }),

    // -------------------------------
    // Device Token / VoIP Registration
    // -------------------------------
    registerVoipToken: builder.mutation({
      query: ({ deviceUuid, extensionUuid, voipToken }) => ({
        url: '/voip/device/register',
        method: 'post',
        data: {
          device_uuid: deviceUuid,
          extension_uuid: extensionUuid,
          voip_token: voipToken,
        },
      }),
    }),

    // -------------------------------
    // Call Detail Records (CDRs) by extension
    // -------------------------------
    getRecentCalls: builder.query({
      query: (includeRinggroups = true) => ({
        url: '/voip/recentcalls',
        method: 'get',
        params: {
          include_ringgroups: includeRinggroups,
        },
      }),
      providesTags: ['Voip'],
    }),

    // -------------------------------
    // Extension Assignment
    // -------------------------------
    getAvailableExtensions: builder.query({
      query: () => ({ url: '/voip/extensions/available', method: 'get' }),
      providesTags: ['VoipExtensions'],
    }),

    assignExtension: builder.mutation({
      query: ({ userId, extension, password }) => ({
        url: '/voip/extensions/assign',
        method: 'put',
        data: { userId, extension, password },
      }),
      invalidatesTags: ['VoipExtensions'],
    }),

    unassignExtension: builder.mutation({
      query: ({ userId }) => ({
        url: `/voip/extensions/assign/${userId}`,
        method: 'delete',
      }),
      invalidatesTags: ['VoipExtensions'],
    }),

    // -------------------------------
    // TURN / ICE Credentials
    // -------------------------------
    getTurnServers: builder.mutation({
      query: () => ({
        url: '/voip/turnservers',
        method: 'get',
      }),
    }),
  }),
});

export const {
  useGetDomainsQuery,
  useGetDomainByIdQuery,
  useGetExtensionsByDomainQuery,
  useGetExtensionByIdQuery,
  useGetVoicemailsByDomainQuery,
  useGetVoicemailsByExtensionQuery,
  useLazyGetVoicemailPlayUrlQuery,
  useGetCdrsQuery,
  useGetRecentCallsQuery,
  useRegisterVoipTokenMutation,
  useGetTurnServersMutation,
  useGetAvailableExtensionsQuery,
  useAssignExtensionMutation,
  useUnassignExtensionMutation,
} = voipSlice;

export default voipSlice;
